import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal, SectionHeader } from "@/components/ui-blocks/Reveal";
import { api, endpoints } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Users, ArrowRight } from "lucide-react";
import { WhatsAppShare } from "@/components/WhatsAppShare";

export const FeaturedCampaigns = ({ limit = 6 }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(endpoints.campaigns).then(setItems).catch(() => {}); }, []);
  const list = items.slice(0, limit);

  return (
    <section id="campaigns" className="bg-background">
      <div className="container-wide py-16 lg:py-24">
        <SectionHeader eyebrow="Featured Campaigns" title="Where your support goes right now." lead="Live campaigns are curated for urgency, transparency and measurable impact. Every rupee is tracked to a program voucher." />
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {list.map((c, i) => {
            const pct = Math.min(100, Math.round(((c.raised_inr || 0) / (c.goal_inr || 1)) * 100));
            return (
              <Reveal key={c.id} delay={i * 0.05}>
                <div className="group flex flex-col rounded-xl border border-[hsl(var(--border))] overflow-hidden bg-card card-lift h-full" data-testid={`campaign-card-${c.slug}`}>
                  <div className="relative aspect-[16/9] overflow-hidden bg-[hsl(var(--surface-tint))]">
                    <img loading="lazy" src={c.image} alt={c.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <Badge className="badge-orange border">{c.category}</Badge>
                      {c.status === "active" && <Badge className="badge-green border">Active</Badge>}
                      {c.status === "upcoming" && <Badge className="badge-navy border">Upcoming</Badge>}
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="heading-3 text-lg">{c.title}</h3>
                    <p className="text-sm text-[hsl(var(--muted-ink))] mt-2 line-clamp-3">{c.summary}</p>
                    <div className="mt-4 flex items-center gap-4 text-xs text-[hsl(var(--muted-ink))]">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {c.location}</span>
                      <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {Number(c.beneficiaries).toLocaleString("en-IN")} people</span>
                    </div>
                    <div className="mt-4">
                      <Progress value={pct} className="h-2 bg-[hsl(var(--surface-tint))]" />
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <span className="font-semibold text-[hsl(var(--navy))]">₹{Number(c.raised_inr).toLocaleString("en-IN")}<span className="text-[hsl(var(--muted-ink))]"> / ₹{Number(c.goal_inr).toLocaleString("en-IN")}</span></span>
                        <span className="text-[hsl(var(--green))] font-semibold">{pct}%</span>
                      </div>
                    </div>
                    <div className="mt-5 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Link to="/donate"><Button size="sm" className="bg-[hsl(var(--navy))] hover:bg-[hsl(var(--navy-2))] text-background">Donate to campaign</Button></Link>
                        <WhatsAppShare compact title={c.title} path={`/campaigns/${c.slug}`} />
                      </div>
                      <Link to={`/campaigns/${c.slug}`} className="text-sm font-medium text-[hsl(var(--navy))] inline-flex items-center gap-1">Details <ArrowRight className="h-3.5 w-3.5" /></Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
